import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { inquiryApi, vehicleApi, calendarApi, formatApiError } from '../services/api';
import { CheckCircle, XCircle, Warning, ChatCircleDots } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ManualInquiryDialog = ({ open, onClose, onCreated }) => {
  const [vehicles, setVehicles] = useState([]);
  const [availability, setAvailability] = useState({}); // { vehicleId: true/false }
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    vehicle_id: '',
    vehicle_preference: '',
    start_date: '',
    end_date: '',
    message: '',
    internal_note: '',
  });

  const reset = useCallback(() => {
    setForm({
      first_name: '', last_name: '', email: '', phone: '',
      vehicle_id: '', vehicle_preference: '',
      start_date: '', end_date: '', message: '', internal_note: '',
    });
    setAvailability({});
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    (async () => {
      try {
        const res = await vehicleApi.getAll();
        setVehicles((res.data || []).filter((v) => v.status !== 'maintenance'));
      } catch (_) {
        toast.error('Fahrzeuge konnten nicht geladen werden');
      }
    })();
  }, [open, reset]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const datesValid = form.start_date && form.end_date && form.start_date <= form.end_date;

  // Auto-check availability when both dates are set
  useEffect(() => {
    if (!datesValid) {
      setAvailability({});
      return;
    }
    let cancelled = false;
    setCheckingAvail(true);
    calendarApi.getAvailability(form.start_date, form.end_date)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res.data || []).forEach((entry) => {
          map[entry.vehicle.id] = entry.is_available;
        });
        setAvailability(map);
      })
      .catch(() => { if (!cancelled) setAvailability({}); })
      .finally(() => { if (!cancelled) setCheckingAvail(false); });
    return () => { cancelled = true; };
  }, [form.start_date, form.end_date, datesValid]);

  const selectedVehicleAvail = useMemo(() => {
    if (!form.vehicle_id) return null;
    if (!datesValid) return null;
    if (form.vehicle_id in availability) return availability[form.vehicle_id];
    // If vehicle wasn't returned by /availability (status != available), it's effectively unavailable
    return false;
  }, [form.vehicle_id, availability, datesValid]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('Vorname, Nachname und E-Mail sind Pflicht');
      return;
    }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      toast.error('Enddatum darf nicht vor Startdatum liegen');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        vehicle_id: form.vehicle_id || null,
        vehicle_preference: form.vehicle_preference.trim() || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        message: form.message.trim() || null,
        internal_note: form.internal_note.trim() || null,
        source: 'manual',
      };
      const res = await inquiryApi.create(payload);
      toast.success('Anfrage erstellt');
      onCreated && onCreated(res.data);
      onClose();
    } catch (err) {
      const msg = formatApiError(err);
      toast.error(msg || 'Erstellung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="manual-inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <ChatCircleDots weight="fill" size={22} className="text-[#E60000]" />
            Anfrage manuell erstellen
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          {/* Customer */}
          <div>
            <Label className="overline mb-2 block">Kunde</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input placeholder="Vorname *" value={form.first_name}
                onChange={(e) => set('first_name', e.target.value)} data-testid="mi-first-name" />
              <Input placeholder="Nachname *" value={form.last_name}
                onChange={(e) => set('last_name', e.target.value)} data-testid="mi-last-name" />
              <Input placeholder="E-Mail *" type="email" value={form.email}
                onChange={(e) => set('email', e.target.value)} data-testid="mi-email" />
              <Input placeholder="Telefon" value={form.phone}
                onChange={(e) => set('phone', e.target.value)} data-testid="mi-phone" />
            </div>
          </div>

          {/* Period */}
          <div>
            <Label className="overline mb-2 block">Mietzeitraum (optional)</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#525866]">Von</Label>
                <Input type="date" value={form.start_date}
                  onChange={(e) => set('start_date', e.target.value)} data-testid="mi-start-date" />
              </div>
              <div>
                <Label className="text-xs text-[#525866]">Bis</Label>
                <Input type="date" value={form.end_date} min={form.start_date || undefined}
                  onChange={(e) => set('end_date', e.target.value)} data-testid="mi-end-date" />
              </div>
            </div>
          </div>

          {/* Vehicle availability */}
          <div>
            <Label className="overline mb-2 block">Fahrzeug</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#525866]">Konkretes Fahrzeug (optional)</Label>
                <Select value={form.vehicle_id || 'none'} onValueChange={(v) => set('vehicle_id', v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="mi-vehicle-select"><SelectValue placeholder="– kein Fahrzeug zugeordnet –" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">– kein Fahrzeug zugeordnet –</SelectItem>
                    {vehicles.map((v) => {
                      const isAvail = datesValid ? (v.id in availability ? availability[v.id] : false) : null;
                      return (
                        <SelectItem key={v.id} value={v.id}>
                          <span className="flex items-center gap-2">
                            <span>{v.brand} {v.model} <span className="text-xs text-[#8A94A6] font-mono">({v.license_plate})</span></span>
                            {isAvail === true && <CheckCircle weight="fill" size={14} className="text-[#009951]" />}
                            {isAvail === false && <XCircle weight="fill" size={14} className="text-[#E60000]" />}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {form.vehicle_id && datesValid && (
                  <div className="mt-2 text-sm flex items-center gap-2" data-testid="mi-availability-status">
                    {checkingAvail ? (
                      <span className="text-[#8A94A6]">Prüfe Verfügbarkeit…</span>
                    ) : selectedVehicleAvail === true ? (
                      <span className="text-[#009951] flex items-center gap-1"><CheckCircle weight="fill" size={16} /> Verfügbar im gewählten Zeitraum</span>
                    ) : (
                      <span className="text-[#E60000] flex items-center gap-1"><Warning weight="fill" size={16} /> Im Zeitraum bereits gebucht oder nicht verfügbar</span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs text-[#525866]">… oder freier Wunsch (Marke/Modell)</Label>
                <Input placeholder="z.B. SUV, VW Tiguan" value={form.vehicle_preference}
                  onChange={(e) => set('vehicle_preference', e.target.value)} data-testid="mi-vehicle-preference" />
              </div>
            </div>

            {/* Availability summary */}
            {datesValid && (
              <div className="mt-3 p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm text-sm" data-testid="mi-availability-summary">
                {checkingAvail ? (
                  <span className="text-[#8A94A6]">Lade Verfügbarkeiten…</span>
                ) : (
                  <>
                    <strong className="text-[#111418]">Verfügbarkeit im Zeitraum:</strong>{' '}
                    <span className="text-[#009951]">
                      {Object.values(availability).filter(Boolean).length} verfügbar
                    </span>
                    {' • '}
                    <span className="text-[#E60000]">
                      {vehicles.length - Object.values(availability).filter(Boolean).length} belegt/nicht frei
                    </span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Message + Internal note */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="overline mb-1 block">Nachricht des Kunden</Label>
              <textarea
                rows={3}
                className="w-full border border-[#E2E4E9] rounded-sm p-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#E60000]"
                value={form.message}
                onChange={(e) => set('message', e.target.value)}
                data-testid="mi-message"
                placeholder="z.B. „Wir benötigen das Fahrzeug für ein Hochzeitswochenende…“"
              />
            </div>
            <div>
              <Label className="overline mb-1 block">Interne Notiz</Label>
              <textarea
                rows={3}
                className="w-full border border-[#E2E4E9] rounded-sm p-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#E60000]"
                value={form.internal_note}
                onChange={(e) => set('internal_note', e.target.value)}
                data-testid="mi-internal-note"
                placeholder="Nur intern sichtbar"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Abbrechen</Button>
            <Button type="submit" className="btn-primary" disabled={submitting} data-testid="mi-submit">
              {submitting ? 'Speichern…' : 'Anfrage erstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManualInquiryDialog;
